import { useMemo, useState, type FormEvent } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import VideoCard from "@/components/feature/VideoCard";
import Pagination from "@/components/base/Pagination";
import EmptyState from "@/components/base/EmptyState";
import { categoryMeta, videos, type VideoType } from "@/mocks/videos";
import { useDocumentMeta } from "@/hooks/useDocumentMeta";
import { useMediaQuery } from "@/hooks/useMediaQuery";

const PER_PAGE = 6;

const TYPE_TABS: { key: "all" | VideoType; label: string }[] = [
  { key: "all", label: "全部" },
  { key: "movie", label: "电影" },
  { key: "tv", label: "电视剧" },
  { key: "short", label: "短视频" },
];

export default function Search() {
  const [params, setParams] = useSearchParams();
  const navigate = useNavigate();
  const isDesktop = useMediaQuery("(min-width: 768px)");

  const query = params.get("query") || "";
  const activeType = (params.get("type") as "all" | VideoType) || "all";
  const page = Math.max(1, Number(params.get("page")) || 1);

  const [input, setInput] = useState(query);

  useDocumentMeta({
    title: query ? `“${query}”的搜索结果-69视频` : "搜索-69视频",
    description: query
      ? `在69视频中搜索“${query}”，为你匹配相关的电影、电视剧与短视频内容。`
      : "在69视频中搜索你感兴趣的电影、电视剧与短视频。",
    keywords: `搜索,${query},69视频,在线视频`,
    canonicalPath: "/search",
  });

  const results = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return [];
    return videos.filter((v) => {
      if (activeType !== "all" && v.type !== activeType) return false;
      const haystack = [v.title, v.region, ...v.genres, ...v.tags, v.director, ...v.cast].join(" ").toLowerCase();
      return haystack.includes(q);
    });
  }, [query, activeType]);

  const totalPages = Math.ceil(results.length / PER_PAGE);
  const visible = isDesktop ? results.slice((page - 1) * PER_PAGE, page * PER_PAGE) : results.slice(0, page * PER_PAGE);

  const submit = (e: FormEvent) => {
    e.preventDefault();
    const q = input.trim();
    if (!q) return;
    navigate(`/search?query=${encodeURIComponent(q)}`);
  };

  const setParam = (key: string, value: string | null) => {
    const next = new URLSearchParams(params);
    if (value === null || value === "") next.delete(key);
    else next.set(key, value);
    if (key !== "page") next.delete("page");
    setParams(next, { replace: false });
  };

  return (
    <div className="w-full">
      <section className="w-full px-4 md:px-8 pt-8 pb-8 bg-background-100 border-b border-background-200/70">
        <h1 className="font-heading font-bold text-2xl md:text-3xl text-foreground-950 mb-5">
          {query ? <>搜索：“{query}”</> : "搜索视频内容"}
        </h1>
        <form onSubmit={submit} className="flex flex-col sm:flex-row gap-3 max-w-2xl" role="search">
          <div className="flex items-center gap-2 bg-background-50 border border-background-200 rounded-md px-4 py-3 flex-1 focus-within:border-primary-400 transition-colors">
            <i className="ri-search-line text-foreground-500 text-lg" aria-hidden="true"></i>
            <input
              type="search"
              value={input}
              onChange={(e) => setInput(e.target.value)}
              placeholder="输入影片名称、类型或标签"
              className="bg-transparent outline-none text-sm w-full text-foreground-900 placeholder:text-foreground-400"
              aria-label="搜索关键词"
            />
          </div>
          <button
            type="submit"
            className="px-8 py-3 rounded-md bg-primary-500 text-background-50 text-sm font-semibold hover:bg-primary-600 transition-colors cursor-pointer whitespace-nowrap"
          >
            搜索
          </button>
        </form>
      </section>

      <div className="w-full px-4 md:px-8 py-8">
        {query && (
          <>
            <div className="flex items-center justify-between gap-4 mb-6 flex-wrap">
              <p className="text-sm text-foreground-600">
                找到 <span className="font-semibold text-foreground-900">{results.length}</span> 条与“{query}”相关的结果
              </p>
              <div className="flex items-center gap-1 bg-background-100 rounded-md p-1 flex-wrap">
                {TYPE_TABS.map((tab) => (
                  <button
                    key={tab.key}
                    type="button"
                    onClick={() => setParam("type", tab.key === "all" ? null : tab.key)}
                    className={`px-3 py-1.5 rounded-md text-sm transition-colors cursor-pointer whitespace-nowrap ${
                      activeType === tab.key ? "bg-primary-500 text-background-50" : "text-foreground-700 hover:text-primary-600"
                    }`}
                  >
                    {tab.label}
                  </button>
                ))}
              </div>
            </div>

            {results.length === 0 ? (
              <EmptyState
                icon="ri-emotion-sad-line"
                title="没有找到相关内容"
                description={`暂时没有与“${query}”匹配的内容，换个关键词试试，或者去首页看看热门推荐。`}
                actionLabel="返回首页"
                onAction={() => navigate("/")}
              />
            ) : (
              <>
                <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-4 md:gap-5">
                  {visible.map((video) => (
                    <VideoCard key={video.id} video={video} />
                  ))}
                </div>
                <div className="hidden md:block">
                  <Pagination current={page} total={totalPages} onChange={(p) => setParam("page", String(p))} />
                </div>
                {!isDesktop && page < totalPages && (
                  <div className="flex justify-center mt-8">
                    <button
                      type="button"
                      onClick={() => setParam("page", String(page + 1))}
                      className="px-8 py-3 rounded-md border border-background-200 text-sm font-medium text-foreground-800 hover:border-primary-400 hover:text-primary-600 transition-colors cursor-pointer whitespace-nowrap"
                    >
                      加载更多
                    </button>
                  </div>
                )}
              </>
            )}
          </>
        )}

        {!query && (
          <div>
            <h2 className="font-heading font-semibold text-lg text-foreground-900 mb-4">热门内容推荐</h2>
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-4 md:gap-5">
              {[...videos]
                .sort((a, b) => b.popularity - a.popularity)
                .slice(0, 5)
                .map((video) => (
                  <VideoCard key={video.id} video={video} />
                ))}
            </div>
            <div className="flex items-center gap-3 mt-8 flex-wrap">
              {(["movie", "tv", "short"] as VideoType[]).map((t) => (
                <Link
                  key={t}
                  to={`/category/${t}`}
                  className="px-4 py-2 rounded-md bg-secondary-100 text-secondary-800 text-sm font-medium hover:bg-secondary-200 transition-colors cursor-pointer whitespace-nowrap"
                >
                  {categoryMeta[t].label}
                </Link>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}